import { LinearProgress, Typography } from '../primitives/index.js'
import { graphHooks } from '@tunasong/graph-lib/react'
import type { FilterFunc } from '@tunasong/models'
import { entitySort, isPersistedEntity } from '@tunasong/models'
import { usePlugins } from '@tunasong/plugin-lib'
import type { Entity, Persisted } from '@tunasong/schemas'
import { entityTypeNames, isEntity } from '@tunasong/schemas'
import type { FC, MouseEvent } from 'react'
import { useCallback, useRef, useState } from 'react'
import { useDrop } from 'react-dnd'
import invariant from 'tiny-invariant'
import { ConfirmDialog } from '../dialog/index.js'
import { isContainerTarget, moveConfirmText, takesChildType } from '../entity/container-drop.js'
import type { EntityListProps } from '../entity/entity-list.js'
import type { EntityListItemProps } from '../entity/entity-list-item.js'
import { ListSkeleton } from '../entity/list-skeleton.js'
import { reorder } from '../entity/reorder.js'
import { useListKeyboardNav } from '../hooks/list-keyboard-nav.js'
import { useSortFilterOptions } from '../hooks/sort.js'
import { useNavigateToEntity } from '../navigation/navigate.js'
import type { DropTarget } from './constants.js'
import { DROP_BOTTOM } from './constants.js'
import DndDivider from './dnd-divider.js'
import { DraggableListItem } from './dnd-list-item.js'
import { List } from '../primitives/index.js'
import styles from './dnd-list.module.css'

export interface DndListProps extends Omit<EntityListProps, 'onChange'> {
  filter?: FilterFunc

  /** Placeholder when there are no items */
  placeholder?: string

  sort?: boolean

  /** With `sort`: list folders first in automatic sorts. @see FilteredParams.foldersFirst */
  foldersFirst?: boolean

  isLoading?: boolean

  isFetching?: boolean

  navigate?: boolean

  // The key to use for sorting, filtering etc.. If provided, the options will be persisted on the device for this key.
  optionsKey?: string

  /** Row layout, passed to every row. @see EntityListItemProps.layout */
  itemLayout?: EntityListItemProps['layout']

  /** `columns` rows only: show the type column. @see EntityListItemProps.showType */
  showItemType?: boolean

  /** Keep every row's overflow button on touch screens. @see EntityListItemProps.alwaysShowActions */
  alwaysShowItemActions?: boolean

  /** Per-row extras for the `columns` layout, e.g. Home's space counts. @see EntityListItemProps.meta */
  getItemProps?(element: Persisted<Entity>): Pick<EntityListItemProps, 'meta' | 'trailing' | 'activityAt'>

  /** A drop onto `target` moves `source` into it (kind B, accept). @default by the plugins' `allowChildrenTypes` */
  isDropTarget?(props: { source?: Entity; target: Entity }): boolean

  /**
   * `target` is a container, so a drop it won't take is refused visibly rather than treated as a re-order (GH #3164).
   * @default by the plugins, unless `isDropTarget` is given, when no row is a container beyond what it accepts
   */
  isContainer?(target: Entity): boolean

  onChange?(elements: Persisted<Entity>[]): void
}

/** Accept drop into folders, but nothing else for now */

export const DndList: FC<DndListProps> = props => {
  const {
    entities,
    actions = true,
    size = 'small',
    selected = [],
    navigate = true,
    isLoading = false,
    isFetching = false,
    optionsKey = 'default',
    placeholder = 'No items',
    sort = false,
    foldersFirst = false,
    itemLayout,
    showItemType,
    alwaysShowItemActions,
    getItemProps,
    onOpen,
    onChange,
    filter: _filter,
    isDropTarget: _isDropTargetProp,
    isContainer: _isContainerProp,
    showBreadcrumbs: _showBreadcrumbs,
    loading: _loading,
    searchable: _searchable,
    controls: _controls,

    ...restProps
  } = props
  const { moveEntity } = graphHooks.useEntityUtil()
  const navigateToEntity = useNavigateToEntity()
  const plugins = usePlugins('all')

  /** The shared kind B rule (GH #3164): a container whose plugin takes the dragged type */
  const isDropTargetDefault = useCallback(
    ({ source, target }: { source?: Entity; target: Entity }) =>
      Boolean(source?.type) && isContainerTarget(target, plugins) && takesChildType(source as Entity, target, plugins),
    [plugins]
  )
  const isDropTarget: typeof isDropTargetDefault = props.isDropTarget ?? isDropTargetDefault
  const isContainer =
    props.isContainer ?? (props.isDropTarget ? () => false : (target: Entity) => isContainerTarget(target, plugins))

  /** Set to confirm message */
  const moveData = useRef<[Persisted<Entity>, Persisted<Entity>] | null>(null)
  const [moving, setMoving] = useState(false)
  const [confirm, setConfirm] = useState<null | string>(null)

  const handleCancel = useCallback(async () => setConfirm(null), [])

  const handleMove = useCallback(async () => {
    invariant(moveData.current, 'moveData is not set')
    invariant(moveData.current, 'moveData is not set')

    const [entity, newParent] = moveData.current
    setMoving(true)
    await moveEntity({ entity, newParent })
    setMoving(false)
    moveData.current = null
    setConfirm(null)
  }, [moveEntity])

  const handleDropped = useCallback(
    (source: Entity, target: DropTarget) => {
      if (isPersistedEntity(target) && isPersistedEntity(source) && isDropTarget({ source, target })) {
        setConfirm(moveConfirmText(source, target))
        moveData.current = [source, target]
        return
      }

      if (onChange) {
        /** Re-order the list */
        const targetIndex = isEntity(target) ? entities.findIndex(e => e.id === target.id) : entities.length - 1
        const sourceIndex = entities.findIndex(e => e.id === source.id)
        onChange(reorder(entities, sourceIndex, targetIndex))
      }
    },
    [entities, isDropTarget, onChange]
  )

  const handleOpen = useCallback(
    (element: Persisted<Entity>) => (ev: MouseEvent) => {
      const navigationMode = ev.shiftKey ? 'dialog' : 'page'
      if (onOpen) {
        onOpen(element, { navigationMode })
      } else if (navigate) {
        navigateToEntity(element, { navigationMode })
      }
    },
    [navigate, navigateToEntity, onOpen]
  )

  const [{ isHovering: isHoveringBottom }, dropBottomRef] = useDrop({
    accept: entityTypeNames as never,
    drop: item => {
      handleDropped(item, DROP_BOTTOM)
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    canDrop: (item: Entity) => true,
    collect: monitor => ({
      isHovering: monitor.isOver({ shallow: true }), // canDrop(monitor.getItem()) &&,
    }),
  })

  const { filter, sortOptions } = useSortFilterOptions(optionsKey)

  const filteredEntities = (filter.length > 0 ? entities.filter(e => filter.includes(e.type)) : entities).filter(
    e => !e.trash
  )

  const entitiesSorted = sort
    ? entitySort({ entities: filteredEntities, sortOptions, typeFilter: filter, foldersFirst })
    : filteredEntities

  const { listProps: keyboardListProps, getItemRef } = useListKeyboardNav()

  if (isLoading && (!entities || entities.length === 0)) {
    return <ListSkeleton />
  }
  if (entities.length === 0) {
    return (
      <div className={styles.emptyState}>
        <Typography variant="caption" className={styles.emptyText}>
          {placeholder}
        </Typography>
      </div>
    )
  }

  /* A plain list, not an ARIA menu: rows are entities to open. Everything rendered inside it is an
   * <li>; the drag/drop affordances are aria-hidden rows so they never count as items. The refetch
   * progress bar stays exposed. */
  return (
    <>
      <List {...keyboardListProps} {...restProps}>
        {entitiesSorted.map((element, index) => (
          <DraggableListItem
            key={element.id}
            {...getItemProps?.(element)}
            size={size}
            selected={selected.includes(element)}
            actions={actions}
            layout={itemLayout}
            showType={showItemType}
            alwaysShowActions={alwaysShowItemActions}
            element={element}
            navRef={getItemRef(index)}
            onDropped={handleDropped}
            isDropTarget={isDropTarget}
            isContainer={isContainer}
            onClick={handleOpen(element)}
          />
        ))}
        {isHoveringBottom ? (
          <li aria-hidden="true">
            <DndDivider />
          </li>
        ) : null}
        <li aria-hidden="true" className={styles.dropSpacer} ref={dropBottomRef as never} />
        {isFetching && entities.length > 0 ? (
          <li>
            <LinearProgress />
          </li>
        ) : null}
      </List>
      <ConfirmDialog
        open={Boolean(confirm)}
        busy={moving}
        title={'Confirm move'}
        text={confirm ?? ''}
        onCancel={handleCancel}
        onConfirm={handleMove}
      />
    </>
  )
}
